import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getEntityId } from 'helpers/entityId';
import {
  Accordion,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Row,
  Table,
  Badge,
  Stack
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { useCallOutCalendarSocket } from 'hooks/useCallOutCalendarSocket';
import { PrintCalendarPdfModal } from './callout-calendar/components/PrintCalendarPdfModal';
import { CalendarMonthGrid } from './callout-calendar/components/CalendarMonthGrid';
import { SubstituteAssigneeModal } from './callout-calendar/components/SubstituteAssigneeModal';
import {
  createAssignment,
  deleteAssignment,
  generateRotation,
  getAssignments,
  getStats,
  OnCallAssignment,
  OnCallStat,
  substituteAssignment
} from 'services/api/callout';
import { GET_EMPLOYEES_QUERY } from 'services/api/employee';
import { Employee } from 'data/users';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSIONS } from 'constants/permissions';
import { useAccessToken } from 'providers/AccessTokenProvider';
import {
  ColorConfig,
  ManualAssignmentState,
  PdfDayCell,
  PdfMonthModel,
  PdfMultiMonthModel,
  RotationState,
  ScheduleFilters,
  SubstituteState
} from './callout-calendar/types';
import { PRESET_COLORS } from './callout-calendar/types';
import {
  colorToCss,
  makeDefaultColorConfig
} from './callout-calendar/utils/colors';

const formatRange = (start: string, end: string) =>
  `${dayjs(start).format('MMM D, YYYY')} → ${dayjs(end).format('MMM D, YYYY')}`;

const DEFAULT_ROTATION_TIME = '08:00';

const formatHandoffTime = (timeHHmm: string): string => {
  const [hhRaw, mmRaw] = timeHHmm.split(':');
  const hh = Number(hhRaw);
  const mm = Number(mmRaw);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return '8 AM';
  const hour12 = ((hh + 11) % 12) + 1;
  const ampm = hh >= 12 ? 'PM' : 'AM';
  if (mm === 0) return `${hour12} ${ampm}`;
  return `${hour12}:${String(mm).padStart(2, '0')} ${ampm}`;
};

const getLocalTimeZoneLabel = (): string => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short'
    }).formatToParts(new Date());
    return (
      parts.find(p => p.type === 'timeZoneName')?.value ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      'Local'
    );
  } catch {
    return 'Local';
  }
};

const extractTimeHHmm = (localDateTime: string): string => {
  const m = /^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2})$/.exec(localDateTime || '');
  if (!m) return DEFAULT_ROTATION_TIME;
  return `${m[1]}:${m[2]}`;
};

const CallOutCal: React.FC = () => {
  const year = dayjs().year();
  const queries = useQueryClient();
  const { isAdmin } = useAccessToken();

  const [canRead, canCreate, canUpdate, canDelete] = usePermissions([
    PERMISSIONS.CALL_OUT_CALENDAR.READ,
    PERMISSIONS.CALL_OUT_CALENDAR.CREATE,
    PERMISSIONS.CALL_OUT_CALENDAR.UPDATE,
    PERMISSIONS.CALL_OUT_CALENDAR.DELETE
  ]) as boolean[];

  const canWrite =
    Boolean(isAdmin) ||
    Boolean(canCreate) ||
    Boolean(canUpdate) ||
    Boolean(canDelete);

  const buildRotationStartDate = (dateOverride?: string) => {
    const date = dateOverride || dayjs().format('YYYY-MM-DD');
    const [hh = '00', mm = '00'] = DEFAULT_ROTATION_TIME.split(':');
    return `${date}T${String(hh).padStart(2, '0')}:${String(mm).padStart(
      2,
      '0'
    )}`;
  };

  const [manual, setManual] = useState<ManualAssignmentState>({
    assignees: [] as string[],
    startDate: '',
    endDate: '',
    notes: '',
    allowOverlap: false
  });
  const [manualColors, setManualColors] = useState<Record<string, ColorConfig>>(
    {}
  );
  const [rotation, setRotation] = useState<RotationState>({
    pool: [] as string[],
    startDate: buildRotationStartDate(),
    durationDays: 7,
    periods: 8,
    throughYearEnd: false,
    assigneesPerRotation: 1,
    allowOverlap: false
  });
  const [poolColors, setPoolColors] = useState<Record<string, ColorConfig>>({});

  const { data: employeesRes } = useQuery(
    'callout-users',
    GET_EMPLOYEES_QUERY,
    {
      enabled: canWrite
    }
  );
  const callOutEligible = useMemo(() => {
    return employeesRes?.data?.filter((u: Employee) => u.isCallOutUser) || [];
  }, [employeesRes]);

  const { data: assignmentsRes, isLoading: assignmentsLoading } = useQuery(
    ['callout-assignments'],
    () => getAssignments(),
    { enabled: Boolean(isAdmin) || Boolean(canRead) || canWrite }
  );

  const { data: statsRes } = useQuery(
    ['callout-stats', year],
    () => getStats(year),
    { enabled: canWrite }
  );

  const createMutation = useMutation(createAssignment, {
    onSuccess: () => {
      toast.success('Assignment saved');
      queries.invalidateQueries(['callout-assignments']);
    },
    onError: (err: any) => {
      const overlaps = err?.response?.data?.overlaps;
      if (overlaps?.length) {
        toast.error('Overlap detected. Confirm allow overlap to proceed.');
      } else {
        toast.error(
          err?.response?.data?.message || 'Unable to save assignment'
        );
      }
    }
  });

  const rotationMutation = useMutation(generateRotation, {
    onSuccess: () => {
      toast.success('Rotation generated');
      queries.invalidateQueries(['callout-assignments']);
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || 'Unable to generate rotation'
      );
    }
  });

  const deleteMutation = useMutation<void, unknown, string>(deleteAssignment, {
    onSuccess: () => {
      toast.success('Assignment removed');
      queries.invalidateQueries(['callout-assignments']);
    },
    onError: () => {
      toast.error('Unable to delete assignment');
    }
  });

  const [substitute, setSubstitute] = useState<SubstituteState>({
    assignmentId: '',
    replaceUserId: '',
    substituteUserId: '',
    startDate: '',
    endDate: ''
  });
  const [substituteColor, setSubstituteColor] = useState<ColorConfig>(
    makeDefaultColorConfig(0)
  );

  const substituteMutation = useMutation(substituteAssignment, {
    onSuccess: () => {
      toast.success('Substitution saved');
      setSubstitute({
        assignmentId: '',
        replaceUserId: '',
        substituteUserId: '',
        startDate: '',
        endDate: ''
      });
      queries.invalidateQueries(['callout-assignments']);
      queries.invalidateQueries(['callout-stats', year]);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Unable to substitute');
    }
  });

  const assignments: OnCallAssignment[] = assignmentsRes || [];
  const stats: OnCallStat[] = statsRes || [];

  useCallOutCalendarSocket({
    enabled: true,
    onUpdated: () => {
      queries.invalidateQueries(['callout-assignments']);
      queries.invalidateQueries(['callout-stats']);
    }
  });

  const sortedAssignments = [...assignments].sort(
    (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()
  );

  const [scheduleFiltersDraft, setScheduleFiltersDraft] =
    useState<ScheduleFilters>({
      assigneeIds: [] as string[],
      start: '',
      end: ''
    });
  const [scheduleFilters, setScheduleFilters] = useState<ScheduleFilters>({
    assigneeIds: [] as string[],
    start: '',
    end: ''
  });
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<
    Record<string, boolean>
  >({});

  const filteredScheduleAssignments = useMemo(() => {
    const start = scheduleFilters.start
      ? dayjs(scheduleFilters.start).startOf('day')
      : null;
    const end = scheduleFilters.end
      ? dayjs(scheduleFilters.end).endOf('day')
      : null;
    const assignees = scheduleFilters.assigneeIds;

    return sortedAssignments.filter(a => {
      const aStart = dayjs(a.startDate);
      const aEnd = dayjs(a.endDate);

      if (start && aEnd.isBefore(start)) return false;
      if (end && aStart.isAfter(end)) return false;

      if (assignees.length) {
        const hasAssignee = a.assignees.some(u =>
          assignees.includes(getEntityId(u) ?? '')
        );
        if (!hasAssignee) return false;
      }

      return true;
    });
  }, [
    scheduleFilters.assigneeIds,
    scheduleFilters.end,
    scheduleFilters.start,
    sortedAssignments
  ]);

  const hasActiveScheduleFilters =
    scheduleFilters.assigneeIds.length > 0 ||
    Boolean(scheduleFilters.start) ||
    Boolean(scheduleFilters.end);

  const scheduleByMonth = useMemo(() => {
    // Group by the month of the assignment start date (simple + predictable).
    const groups: Record<string, OnCallAssignment[]> = {};
    filteredScheduleAssignments.forEach(a => {
      const key = dayjs(a.startDate).format('YYYY-MM');
      (groups[key] ||= []).push(a);
    });
    const sortedKeys = Object.keys(groups).sort();
    return sortedKeys.map(key => ({
      key,
      label: dayjs(`${key}-01`).format('MMMM YYYY'),
      items: groups[key].sort(
        (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()
      )
    }));
  }, [filteredScheduleAssignments]);

  const selectedIds = useMemo(
    () =>
      Object.keys(selectedAssignmentIds).filter(
        id => selectedAssignmentIds[id]
      ),
    [selectedAssignmentIds]
  );

  const bulkDeleteMutation = useMutation(
    async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map(id => deleteAssignment(id))
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;
      return { succeeded, failed };
    },
    {
      onSuccess: ({ succeeded, failed }) => {
        if (succeeded) toast.success(`Deleted ${succeeded} assignment(s)`);
        if (failed) toast.error(`Failed to delete ${failed} assignment(s)`);
        setSelectedAssignmentIds({});
        queries.invalidateQueries(['callout-assignments']);
      },
      onError: () => {
        toast.error('Bulk delete failed');
      }
    }
  );

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manual.assignees.length) {
      toast.error('Select at least one assignee');
      return;
    }
    const startUtcMs = parseLocalDateTimeToUtcMs(manual.startDate);
    // Interpret end as inclusive end-of-minute since the input is minute-precision.
    const endUtcMs = parseLocalDateTimeToUtcMs(manual.endDate) + 60 * 1000 - 1;
    if (!Number.isFinite(startUtcMs) || !Number.isFinite(endUtcMs)) {
      toast.error('Invalid start/end date');
      return;
    }
    const assigneeColors: Record<string, string> = {};
    manual.assignees.forEach((id, idx) => {
      const cfg = manualColors[id] || makeDefaultColorConfig(idx);
      assigneeColors[id] = colorToCss(cfg);
    });
    createMutation.mutate({
      ...manual,
      assignees: manual.assignees,
      startDate: new Date(startUtcMs).toISOString(),
      endDate: new Date(endUtcMs).toISOString(),
      assigneeColors
    });
  };

  const handleRotationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!rotation.pool.length) {
      toast.error('Add at least one user to the pool');
      return;
    }
    const rotationColors: Record<string, string> = {};
    rotation.pool.forEach((id, idx) => {
      const cfg = poolColors[id] || makeDefaultColorConfig(idx);
      rotationColors[id] = colorToCss(cfg);
    });
    const rotationStartUtcMs = parseLocalDateTimeToUtcMs(rotation.startDate);
    const rotationStartIso = Number.isFinite(rotationStartUtcMs)
      ? new Date(rotationStartUtcMs).toISOString()
      : rotation.startDate;
    rotationMutation.mutate({
      ...rotation,
      startDate: rotationStartIso,
      poolColors: rotationColors
    });
  };

  const [monthsToShow, setMonthsToShow] = useState<number>(12);
  const calendarLoadMoreRef = useRef<HTMLDivElement | null>(null);

  const [printModalOpen, setPrintModalOpen] = useState(false);

  const calendarMonths = useMemo(() => {
    const start = dayjs().startOf('month');
    return Array.from({ length: monthsToShow }).map((_, idx) =>
      start.add(idx, 'month')
    );
  }, [monthsToShow]);

  // Auto-load more months when the user scrolls near the bottom of the calendar.
  useEffect(() => {
    const target = calendarLoadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        const hit = entries.some(e => e.isIntersecting);
        if (hit) {
          setMonthsToShow(m => m + 6);
        }
      },
      { root: null, rootMargin: '400px', threshold: 0.01 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const getMonthDays = (month: dayjs.Dayjs) => {
    const start = month.startOf('month').startOf('week');
    const end = month.endOf('month').endOf('week');
    const days: dayjs.Dayjs[] = [];
    let cursor = start;
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
      days.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    return days;
  };

  const DAY_MS = 24 * 60 * 60 * 1000;

  function formatLocalDateTimeInput(msUtc: number): string {
    const d = new Date(msUtc);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${da}T${hh}:${mm}`;
  }

  function parseLocalDateTimeToUtcMs(v: string): number {
    // Interpret "YYYY-MM-DDTHH:mm" as viewer-local wall time -> UTC ms.
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(v || '');
    if (!m) return NaN;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const hh = Number(m[4]);
    const mm = Number(m[5]);
    if (
      !Number.isFinite(y) ||
      !Number.isFinite(mo) ||
      !Number.isFinite(d) ||
      !Number.isFinite(hh) ||
      !Number.isFinite(mm)
    ) {
      return NaN;
    }
    return new Date(y, mo - 1, d, hh, mm, 0, 0).getTime();
  }

  const getLocalDayWindowUtcMs = (ymd: string) => {
    const timeHHmm = extractTimeHHmm(rotation.startDate);
    const [hhStr = '00', mmStr = '00'] = timeHHmm.split(':');
    const hh = Number(hhStr);
    const mm = Number(mmStr);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
    if (!m) {
      return { startUtcMs: NaN, endUtcMs: NaN };
    }
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const startUtcMs = new Date(y, mo - 1, d, hh, mm, 0, 0).getTime();
    const endUtcMs = startUtcMs + DAY_MS - 1;
    return { startUtcMs, endUtcMs };
  };

  const assignmentsForDay = (day: dayjs.Dayjs) => {
    const { startUtcMs, endUtcMs } = getLocalDayWindowUtcMs(
      day.format('YYYY-MM-DD')
    );
    return sortedAssignments.filter(a => {
      const startMs = Date.parse(String(a.startDate));
      const endMs = Date.parse(String(a.endDate));
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false;
      return startMs <= endUtcMs && endMs >= startUtcMs;
    });
  };

  const getAssigneeColor = (
    colors: unknown,
    userId: string
  ): string | undefined => {
    if (!colors) return undefined;
    const maybeMap = colors as any;
    if (typeof maybeMap.get === 'function') {
      return maybeMap.get(userId);
    }
    return (colors as Record<string, string>)[userId];
  };

  const openSubstituteForAssignment = (
    assignmentId: string,
    day?: dayjs.Dayjs
  ) => {
    const assignment = sortedAssignments.find(
      a => (getEntityId(a) ?? '') === assignmentId
    );
    if (!assignment) return;

    const start = day
      ? (() => {
          const { startUtcMs } = getLocalDayWindowUtcMs(
            day.format('YYYY-MM-DD')
          );
          return formatLocalDateTimeInput(startUtcMs);
        })()
      : dayjs(assignment.startDate).format('YYYY-MM-DDTHH:mm');
    const end = day
      ? (() => {
          const { endUtcMs } = getLocalDayWindowUtcMs(day.format('YYYY-MM-DD'));
          return formatLocalDateTimeInput(endUtcMs);
        })()
      : dayjs(assignment.endDate).format('YYYY-MM-DDTHH:mm');

    setSubstitute({
      assignmentId: getEntityId(assignment) ?? '',
      replaceUserId: getEntityId(assignment.assignees?.[0]) || '',
      substituteUserId: '',
      startDate: start,
      endDate: end
    });
    setSubstituteColor(makeDefaultColorConfig(0));
  };

  const resetSubstituteModal = () => {
    setSubstitute({
      assignmentId: '',
      replaceUserId: '',
      substituteUserId: '',
      startDate: '',
      endDate: ''
    });
  };

  const submitSubstitution = () => {
    const cssColor = colorToCss(substituteColor);
    const startUtcMs = parseLocalDateTimeToUtcMs(substitute.startDate);
    // Interpret end as inclusive end-of-minute since the input is minute-precision.
    // Prevents the backend from creating a tiny trailing segment for the old assignee.
    const endUtcMs =
      parseLocalDateTimeToUtcMs(substitute.endDate) + 60 * 1000 - 1;
    const startIso = new Date(startUtcMs).toISOString();
    const endIso = new Date(endUtcMs).toISOString();

    substituteMutation.mutate({
      id: substitute.assignmentId,
      payload: {
        replaceUserId: substitute.replaceUserId,
        substituteUserId: substitute.substituteUserId,
        startDate: startIso,
        endDate: endIso,
        assigneeColors: { [substitute.substituteUserId]: cssColor }
      }
    });
  };

  const pdfAllMonthsModel: PdfMultiMonthModel = useMemo(() => {
    const generateMonthModel = (month: dayjs.Dayjs): PdfMonthModel => {
      const monthLabel = month.format('MMMM YYYY');
      const monthDays = getMonthDays(month);
      const weekCount = Math.ceil(monthDays.length / 7);
      const weeks: PdfDayCell[][] = [];

      for (let wIdx = 0; wIdx < weekCount; wIdx++) {
        const weekDays = monthDays.slice(wIdx * 7, wIdx * 7 + 7);
        const cells: PdfDayCell[] = weekDays.map(day => {
          const inMonth = day.month() === month.month();
          const dayAssignments = assignmentsForDay(day);
          const lines: Array<{ text: string; color?: string }> = [];

          dayAssignments.forEach(a => {
            a.assignees.forEach(u => {
              const color = getAssigneeColor(
                (a as any).assigneeColors,
                getEntityId(u) ?? ''
              );
              lines.push({
                text: `${u.firstName} ${u.lastName}`,
                color
              });
            });
          });

          if (!lines.length) lines.push({ text: '—', color: '#999' });

          return {
            key: day.format('YYYY-MM-DD'),
            dayNumber: day.date(),
            inMonth,
            lines
          };
        });
        weeks.push(cells);
      }

      return { monthLabel, weekRows: weeks };
    };

    // Find all unique months that have assignments
    const monthsWithAssignments = new Set<string>();

    sortedAssignments.forEach(a => {
      const startDate = dayjs(a.startDate);
      const endDate = dayjs(a.endDate);

      // Add all months between start and end dates
      let current = startDate.startOf('month');
      const end = endDate.endOf('month');

      while (current.isBefore(end) || current.isSame(end, 'month')) {
        monthsWithAssignments.add(current.format('YYYY-MM'));
        current = current.add(1, 'month');
      }
    });

    // Convert to sorted array and generate models
    const sortedMonthKeys = Array.from(monthsWithAssignments).sort();

    if (sortedMonthKeys.length === 0) {
      // If no assignments, show current month
      return {
        months: [generateMonthModel(dayjs().startOf('month'))]
      };
    }

    const months = sortedMonthKeys.map(key => {
      const month = dayjs(`${key}-01`);
      return generateMonthModel(month);
    });

    return { months };
  }, [sortedAssignments, rotation.startDate]);

  const calendarCard = (
    <Card className="shadow-sm border-0 mb-4">
      <Card.Header className="border-0">
        <Card.Title className="mb-1">Call-Out Calendar</Card.Title>
        <small className="text-600">
          {canWrite
            ? 'Scrollable month view (admin/editor)'
            : 'Scrollable month view'}
        </small>
      </Card.Header>
      <Card.Body className="p-0">
        <CalendarMonthGrid
          calendarMonths={calendarMonths}
          getMonthDays={getMonthDays}
          assignmentsForDay={assignmentsForDay}
          assignmentsLoading={Boolean(assignmentsLoading)}
          canWrite={Boolean(canWrite)}
          openSubstituteForAssignment={openSubstituteForAssignment}
          calendarLoadMoreRef={calendarLoadMoreRef}
          onLoadMoreMonths={() => setMonthsToShow(m => m + 6)}
        />
      </Card.Body>
    </Card>
  );

  return (
    <div className="mb-6">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="text-uppercase text-700 fw-semibold mb-1">
            Resource Center
          </p>
          <h2 className="mb-0">Call-Out Calendar</h2>
        </div>
        <div className="text-end">
          <div className="fw-semibold text-700">
            {getLocalTimeZoneLabel()} ·{' '}
            {formatHandoffTime(extractTimeHHmm(rotation.startDate))} handoff
          </div>
          <div className="mt-2 d-flex justify-content-end align-items-center gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              className="callout-calendar-print-hide"
              onClick={() => {
                setPrintModalOpen(true);
              }}
            >
              Print calendar
            </Button>
          </div>
        </div>
      </div>

      {canWrite && (
        <Row className="g-3 mb-4">
          {stats.map(stat => (
            <Col key={getEntityId(stat.user) ?? ''} xl={3} md={4} sm={6}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body>
                  <Stack
                    direction="horizontal"
                    className="justify-content-between mb-2"
                  >
                    <div className="fw-semibold">
                      {stat.user.firstName} {stat.user.lastName}
                    </div>
                    <Badge bg="info" pill>
                      {stat.days.toFixed(1)} days YTD
                    </Badge>
                  </Stack>
                  <div className="d-flex justify-content-between text-700">
                    <div>
                      <div className="fw-semibold">
                        {stat.longestStreak} days
                      </div>
                      <small className="text-600">Longest streak</small>
                    </div>
                    <div className="text-end">
                      <div className="fw-semibold">
                        {stat.currentStreak} days
                      </div>
                      <small className="text-600">Current streak</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {!canWrite && calendarCard}

      {canWrite && (
        <Row className="g-3 mb-4">
          <Col lg={7}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Header className="border-0 pb-0">
                <Card.Title className="mb-1">Auto-rotation</Card.Title>
                <small className="text-600">
                  Rotate the pool every {rotation.durationDays} days for the
                  selected window.
                </small>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleRotationSubmit} className="row g-3">
                  <Col md={6}>
                    <Form.Label>Call-Out pool</Form.Label>
                    <Form.Select
                      multiple
                      value={rotation.pool}
                      onChange={e =>
                        (() => {
                          const selected = Array.from(
                            e.target.selectedOptions
                          ).map(o => o.value);
                          setRotation({ ...rotation, pool: selected });
                          setPoolColors(prev => {
                            const next: Record<string, ColorConfig> = {};
                            selected.forEach((id, idx) => {
                              next[id] =
                                prev[id] || makeDefaultColorConfig(idx);
                            });
                            return next;
                          });
                        })()
                      }
                    >
                      {callOutEligible.map(user => (
                        <option
                          key={getEntityId(user) ?? ''}
                          value={getEntityId(user) ?? ''}
                        >
                          {user.firstName} {user.lastName}
                        </option>
                      ))}
                    </Form.Select>
                    {rotation.pool.length > 0 && (
                      <div className="mt-3">
                        <div className="text-700 fw-semibold mb-2">
                          Pool order & colors (1 → N)
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {rotation.pool.map((id, idx) => {
                            const user = callOutEligible.find(
                              u => (getEntityId(u) ?? '') === id
                            );
                            const cfg =
                              poolColors[id] || makeDefaultColorConfig(idx);
                            const preview = colorToCss(cfg);
                            return (
                              <div
                                key={id}
                                className="d-flex align-items-center justify-content-between gap-2 flex-wrap"
                              >
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <Badge bg="secondary" pill>
                                    {idx + 1}
                                  </Badge>
                                  <Badge
                                    // Avoid default bg='primary' when we want custom colors.
                                    bg={preview ? '' : 'secondary'}
                                    pill
                                    style={{
                                      backgroundColor: preview,
                                      border: '1px solid rgba(0,0,0,0.15)'
                                    }}
                                  >
                                    {user
                                      ? `${user.firstName} ${user.lastName}`
                                      : id}
                                  </Badge>
                                </div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <div className="d-flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      disabled={idx === 0}
                                      onClick={() => {
                                        const next = [...rotation.pool];
                                        const tmp = next[idx - 1];
                                        next[idx - 1] = next[idx];
                                        next[idx] = tmp;
                                        setRotation({
                                          ...rotation,
                                          pool: next
                                        });
                                        setPoolColors(prev => {
                                          const updated: Record<
                                            string,
                                            ColorConfig
                                          > = {};
                                          next.forEach((userId, orderIdx) => {
                                            updated[userId] =
                                              prev[userId] ||
                                              makeDefaultColorConfig(orderIdx);
                                          });
                                          return updated;
                                        });
                                      }}
                                    >
                                      Up
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      disabled={
                                        idx === rotation.pool.length - 1
                                      }
                                      onClick={() => {
                                        const next = [...rotation.pool];
                                        const tmp = next[idx + 1];
                                        next[idx + 1] = next[idx];
                                        next[idx] = tmp;
                                        setRotation({
                                          ...rotation,
                                          pool: next
                                        });
                                        setPoolColors(prev => {
                                          const updated: Record<
                                            string,
                                            ColorConfig
                                          > = {};
                                          next.forEach((userId, orderIdx) => {
                                            updated[userId] =
                                              prev[userId] ||
                                              makeDefaultColorConfig(orderIdx);
                                          });
                                          return updated;
                                        });
                                      }}
                                    >
                                      Down
                                    </Button>
                                  </div>
                                  <Form.Select
                                    size="sm"
                                    style={{ maxWidth: 180 }}
                                    value={
                                      cfg.mode === 'preset'
                                        ? cfg.presetKey
                                        : 'custom'
                                    }
                                    onChange={e => {
                                      const v = e.target.value;
                                      setPoolColors(prev => {
                                        const next = { ...prev };
                                        if (v === 'custom') {
                                          next[id] = {
                                            mode: 'custom',
                                            r: 255,
                                            g: 0,
                                            b: 0
                                          };
                                        } else {
                                          next[id] = {
                                            mode: 'preset',
                                            presetKey: v as any
                                          };
                                        }
                                        return next;
                                      });
                                    }}
                                  >
                                    {Object.entries(PRESET_COLORS).map(
                                      ([k, p]) => (
                                        <option key={k} value={k}>
                                          {p.label}
                                        </option>
                                      )
                                    )}
                                    <option value="custom">Custom (RGB)</option>
                                  </Form.Select>
                                  {cfg.mode === 'custom' && (
                                    <div className="d-flex align-items-center gap-2">
                                      <Form.Control
                                        size="sm"
                                        type="number"
                                        min={0}
                                        max={255}
                                        value={cfg.r}
                                        style={{ width: 90 }}
                                        onChange={e =>
                                          setPoolColors(prev => ({
                                            ...prev,
                                            [id]: {
                                              mode: 'custom',
                                              r: Number(e.target.value),
                                              g: cfg.g,
                                              b: cfg.b
                                            }
                                          }))
                                        }
                                      />
                                      <Form.Control
                                        size="sm"
                                        type="number"
                                        min={0}
                                        max={255}
                                        value={cfg.g}
                                        style={{ width: 90 }}
                                        onChange={e =>
                                          setPoolColors(prev => ({
                                            ...prev,
                                            [id]: {
                                              mode: 'custom',
                                              r: cfg.r,
                                              g: Number(e.target.value),
                                              b: cfg.b
                                            }
                                          }))
                                        }
                                      />
                                      <Form.Control
                                        size="sm"
                                        type="number"
                                        min={0}
                                        max={255}
                                        value={cfg.b}
                                        style={{ width: 90 }}
                                        onChange={e =>
                                          setPoolColors(prev => ({
                                            ...prev,
                                            [id]: {
                                              mode: 'custom',
                                              r: cfg.r,
                                              g: cfg.g,
                                              b: Number(e.target.value)
                                            }
                                          }))
                                        }
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <small className="text-600 d-block mt-2">
                          RGB inputs are (R, G, B) from 0–255.
                        </small>
                        <small className="text-600 d-block">
                          Rotation will cycle in this exact order.
                        </small>
                      </div>
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Label>Start date & time</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={rotation.startDate}
                      onChange={e =>
                        setRotation({ ...rotation, startDate: e.target.value })
                      }
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Days per block</Form.Label>
                    <Form.Control
                      type="number"
                      value={rotation.durationDays}
                      min={1}
                      onChange={e =>
                        setRotation({
                          ...rotation,
                          durationDays: Number(e.target.value)
                        })
                      }
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Periods</Form.Label>
                    <Form.Control
                      type="number"
                      value={rotation.periods}
                      min={1}
                      onChange={e =>
                        setRotation({
                          ...rotation,
                          periods: Number(e.target.value)
                        })
                      }
                      disabled={rotation.throughYearEnd}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Assignees per block</Form.Label>
                    <Form.Control
                      type="number"
                      value={rotation.assigneesPerRotation}
                      min={1}
                      onChange={e =>
                        setRotation({
                          ...rotation,
                          assigneesPerRotation: Number(e.target.value)
                        })
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="through-year"
                      label="Generate through year end"
                      checked={rotation.throughYearEnd}
                      onChange={e =>
                        setRotation({
                          ...rotation,
                          throughYearEnd: e.target.checked
                        })
                      }
                    />
                    <Form.Check
                      type="switch"
                      id="allow-overlap-rotation"
                      label="Allow overlaps"
                      checked={rotation.allowOverlap}
                      onChange={e =>
                        setRotation({
                          ...rotation,
                          allowOverlap: e.target.checked
                        })
                      }
                    />
                  </Col>
                  <Col className="text-end">
                    <Button
                      type="submit"
                      disabled={rotationMutation.isLoading}
                      variant="primary"
                    >
                      {rotationMutation.isLoading
                        ? 'Generating…'
                        : 'Generate rotation'}
                    </Button>
                  </Col>
                </Form>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={5}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Header className="border-0 pb-0">
                <Card.Title className="mb-1">Manual assignment</Card.Title>
                <small className="text-600">
                  Assign one or more call-out users for any date range.
                </small>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleManualSubmit} className="g-3 row">
                  <Col xs={12}>
                    <Form.Label>Assignees</Form.Label>
                    <Form.Select
                      multiple
                      value={manual.assignees}
                      onChange={e =>
                        (() => {
                          const selected = Array.from(
                            e.target.selectedOptions
                          ).map(o => o.value);
                          setManual(prev => ({ ...prev, assignees: selected }));
                          setManualColors(prev => {
                            const next: Record<string, ColorConfig> = {};
                            selected.forEach((id, idx) => {
                              next[id] =
                                prev[id] || makeDefaultColorConfig(idx);
                            });
                            return next;
                          });
                        })()
                      }
                      required
                    >
                      {callOutEligible.map(user => (
                        <option
                          key={getEntityId(user) ?? ''}
                          value={getEntityId(user) ?? ''}
                        >
                          {user.firstName} {user.lastName}
                        </option>
                      ))}
                    </Form.Select>
                    {manual.assignees.length > 0 && (
                      <div className="mt-3">
                        <div className="text-700 fw-semibold mb-2">
                          Assignment colors
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {manual.assignees.map((id, idx) => {
                            const user = callOutEligible.find(
                              u => (getEntityId(u) ?? '') === id
                            );
                            const cfg =
                              manualColors[id] || makeDefaultColorConfig(idx);
                            const preview = colorToCss(cfg);
                            return (
                              <div
                                key={id}
                                className="d-flex align-items-center gap-2 flex-wrap"
                              >
                                <Badge
                                  // Avoid default bg='primary' when we want custom colors.
                                  bg={preview ? '' : 'secondary'}
                                  pill
                                  style={{
                                    backgroundColor: preview,
                                    border: '1px solid rgba(0,0,0,0.15)'
                                  }}
                                >
                                  {user
                                    ? `${user.firstName} ${user.lastName}`
                                    : id}
                                </Badge>
                                <Form.Select
                                  size="sm"
                                  style={{ maxWidth: 180 }}
                                  value={
                                    cfg.mode === 'preset'
                                      ? cfg.presetKey
                                      : 'custom'
                                  }
                                  onChange={e => {
                                    const v = e.target.value;
                                    setManualColors(prev => {
                                      const next = { ...prev };
                                      if (v === 'custom') {
                                        next[id] = {
                                          mode: 'custom',
                                          r: 255,
                                          g: 0,
                                          b: 0
                                        };
                                      } else {
                                        next[id] = {
                                          mode: 'preset',
                                          presetKey: v as any
                                        };
                                      }
                                      return next;
                                    });
                                  }}
                                >
                                  {Object.entries(PRESET_COLORS).map(
                                    ([k, p]) => (
                                      <option key={k} value={k}>
                                        {p.label}
                                      </option>
                                    )
                                  )}
                                  <option value="custom">Custom (RGB)</option>
                                </Form.Select>
                                {cfg.mode === 'custom' && (
                                  <div className="d-flex align-items-center gap-2">
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min={0}
                                      max={255}
                                      value={cfg.r}
                                      style={{ width: 90 }}
                                      onChange={e =>
                                        setManualColors(prev => ({
                                          ...prev,
                                          [id]: {
                                            mode: 'custom',
                                            r: Number(e.target.value),
                                            g: cfg.g,
                                            b: cfg.b
                                          }
                                        }))
                                      }
                                    />
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min={0}
                                      max={255}
                                      value={cfg.g}
                                      style={{ width: 90 }}
                                      onChange={e =>
                                        setManualColors(prev => ({
                                          ...prev,
                                          [id]: {
                                            mode: 'custom',
                                            r: cfg.r,
                                            g: Number(e.target.value),
                                            b: cfg.b
                                          }
                                        }))
                                      }
                                    />
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min={0}
                                      max={255}
                                      value={cfg.b}
                                      style={{ width: 90 }}
                                      onChange={e =>
                                        setManualColors(prev => ({
                                          ...prev,
                                          [id]: {
                                            mode: 'custom',
                                            r: cfg.r,
                                            g: cfg.g,
                                            b: Number(e.target.value)
                                          }
                                        }))
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Label>Start</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={manual.startDate}
                      onChange={e =>
                        setManual(prev => ({
                          ...prev,
                          startDate: e.target.value
                        }))
                      }
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>End</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={manual.endDate}
                      onChange={e =>
                        setManual(prev => ({
                          ...prev,
                          endDate: e.target.value
                        }))
                      }
                      required
                    />
                  </Col>
                  <Col xs={12}>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={manual.notes}
                      onChange={e =>
                        setManual(prev => ({ ...prev, notes: e.target.value }))
                      }
                    />
                  </Col>
                  <Col xs={12}>
                    <Form.Check
                      type="switch"
                      id="allow-overlap"
                      label="Allow overlap for this assignment"
                      checked={manual.allowOverlap}
                      onChange={e =>
                        setManual(prev => ({
                          ...prev,
                          allowOverlap: e.target.checked
                        }))
                      }
                    />
                  </Col>
                  <Col className="text-end">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={createMutation.isLoading}
                    >
                      {createMutation.isLoading ? 'Saving…' : 'Add assignment'}
                    </Button>
                  </Col>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {canWrite && (
        <Card className="shadow-sm border-0">
          <Card.Header className="border-0">
            <Card.Title className="mb-1">Schedule & Log</Card.Title>
            <small className="text-600">
              Overlaps allowed by explicit confirmation are marked.
            </small>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="p-3 border-bottom bg-body-tertiary d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Dropdown align="end" className="me-2">
                  <Dropdown.Toggle
                    variant="outline-primary"
                    id="callout-schedule-filters"
                  >
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    Filters
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    className="p-3"
                    style={{ width: '320px', zIndex: 3000 }}
                  >
                    <Form>
                      <Form.Group className="mb-3">
                        <Form.Label>Assignees</Form.Label>
                        <Form.Select
                          multiple
                          value={scheduleFiltersDraft.assigneeIds}
                          onChange={e =>
                            setScheduleFiltersDraft(prev => ({
                              ...prev,
                              assigneeIds: Array.from(
                                e.target.selectedOptions
                              ).map(o => o.value)
                            }))
                          }
                        >
                          {callOutEligible.map(u => (
                            <option
                              key={getEntityId(u) ?? ''}
                              value={getEntityId(u) ?? ''}
                            >
                              {u.firstName} {u.lastName}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text muted>
                          Filter schedule by one or more assignees
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Start date</Form.Label>
                        <Form.Control
                          type="date"
                          value={scheduleFiltersDraft.start}
                          onChange={e =>
                            setScheduleFiltersDraft(prev => ({
                              ...prev,
                              start: e.target.value
                            }))
                          }
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>End date</Form.Label>
                        <Form.Control
                          type="date"
                          value={scheduleFiltersDraft.end}
                          onChange={e =>
                            setScheduleFiltersDraft(prev => ({
                              ...prev,
                              end: e.target.value
                            }))
                          }
                        />
                      </Form.Group>

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            const start = dayjs().format('YYYY-MM-DD');
                            const end = dayjs()
                              .add(13, 'day')
                              .format('YYYY-MM-DD');
                            setScheduleFiltersDraft(prev => ({
                              ...prev,
                              start,
                              end
                            }));
                          }}
                        >
                          Next 14 days
                        </Button>
                        <small className="text-600">
                          {hasActiveScheduleFilters ? 'Active' : 'None'}
                        </small>
                      </div>

                      <div className="d-flex gap-2">
                        <Button
                          className="flex-grow-1"
                          variant="primary"
                          onClick={() => {
                            setScheduleFilters(scheduleFiltersDraft);
                          }}
                        >
                          Apply Filters
                        </Button>
                        <Button
                          className="flex-grow-1"
                          variant="outline-secondary"
                          onClick={() => {
                            const cleared = {
                              assigneeIds: [] as string[],
                              start: '',
                              end: ''
                            };
                            setScheduleFiltersDraft(cleared);
                            setScheduleFilters(cleared);
                            setSelectedAssignmentIds({});
                          }}
                        >
                          Reset
                        </Button>
                      </div>
                    </Form>
                  </Dropdown.Menu>
                </Dropdown>

                <small className="text-600">
                  Showing <strong>{filteredScheduleAssignments.length}</strong>{' '}
                  assignment(s)
                  {hasActiveScheduleFilters && (
                    <>
                      {' '}
                      · <strong>Filtered</strong>
                    </>
                  )}
                </small>
              </div>

              <Button
                size="sm"
                variant="outline-danger"
                disabled={!selectedIds.length || bulkDeleteMutation.isLoading}
                onClick={() => {
                  const ok = window.confirm(
                    `Delete ${selectedIds.length} selected assignment(s)? This cannot be undone.`
                  );
                  if (!ok) return;
                  bulkDeleteMutation.mutate(selectedIds);
                }}
              >
                {bulkDeleteMutation.isLoading
                  ? 'Deleting…'
                  : `Delete selected (${selectedIds.length})`}
              </Button>
            </div>
            <Accordion defaultActiveKey={scheduleByMonth.at(-1)?.key}>
              {assignmentsLoading && (
                <div className="p-4 text-center text-600">Loading…</div>
              )}
              {!assignmentsLoading && scheduleByMonth.length === 0 && (
                <div className="p-4 text-center text-600">
                  No call-out assignments yet.
                </div>
              )}
              {!assignmentsLoading &&
                scheduleByMonth.map(group => {
                  const allChecked =
                    group.items.length > 0 &&
                    group.items.every(
                      a => selectedAssignmentIds[getEntityId(a) ?? '']
                    );
                  const someChecked =
                    group.items.some(
                      a => selectedAssignmentIds[getEntityId(a) ?? '']
                    ) && !allChecked;

                  return (
                    <Accordion.Item eventKey={group.key} key={group.key}>
                      <Accordion.Header>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="fw-semibold">{group.label}</span>
                          <Badge bg="secondary" pill>
                            {group.items.length}
                          </Badge>
                          {someChecked && (
                            <Badge bg="info" pill>
                              Selected
                            </Badge>
                          )}
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="p-0">
                        <Table responsive hover className="mb-0">
                          <thead className="bg-body-tertiary text-700">
                            <tr>
                              <th style={{ width: 52 }}>
                                <Form.Check
                                  type="checkbox"
                                  checked={allChecked}
                                  ref={(el: HTMLInputElement | null) => {
                                    if (el) el.indeterminate = someChecked;
                                  }}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    setSelectedAssignmentIds(prev => {
                                      const next = { ...prev };
                                      group.items.forEach(a => {
                                        next[getEntityId(a) ?? ''] = checked;
                                      });
                                      return next;
                                    });
                                  }}
                                />
                              </th>
                              <th>Assignees</th>
                              <th>Window</th>
                              <th>Duration</th>
                              <th>Auto</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.items.map(item => (
                              <tr key={getEntityId(item) ?? ''}>
                                <td className="align-middle">
                                  <Form.Check
                                    type="checkbox"
                                    checked={Boolean(
                                      selectedAssignmentIds[
                                        getEntityId(item) ?? ''
                                      ]
                                    )}
                                    onChange={e =>
                                      setSelectedAssignmentIds(prev => ({
                                        ...prev,
                                        [getEntityId(item) ?? '']:
                                          e.target.checked
                                      }))
                                    }
                                  />
                                </td>
                                <td className="align-middle">
                                  <Stack
                                    direction="horizontal"
                                    gap={2}
                                    className="flex-wrap"
                                  >
                                    {item.assignees.map(user => {
                                      const color = getAssigneeColor(
                                        (item as any).assigneeColors,
                                        getEntityId(user) ?? ''
                                      );
                                      return (
                                        <Badge
                                          key={getEntityId(user) ?? ''}
                                          // Avoid default bg='primary' when we want custom colors.
                                          bg={color ? '' : 'primary'}
                                          pill
                                          style={
                                            color
                                              ? {
                                                  backgroundColor: color,
                                                  border:
                                                    '1px solid rgba(0, 0, 0, 0.15)'
                                                }
                                              : undefined
                                          }
                                        >
                                          {user.firstName} {user.lastName}
                                        </Badge>
                                      );
                                    })}
                                  </Stack>
                                </td>
                                <td className="align-middle">
                                  {formatRange(item.startDate, item.endDate)}
                                </td>
                                <td className="align-middle">
                                  {dayjs(item.endDate).diff(
                                    dayjs(item.startDate),
                                    'day'
                                  ) + 1}{' '}
                                  days
                                </td>
                                <td className="align-middle">
                                  {item.autoGenerated ? (
                                    <Badge bg="info">Auto</Badge>
                                  ) : (
                                    <Badge bg="secondary">Manual</Badge>
                                  )}
                                </td>
                                <td className="align-middle text-end">
                                  <Button
                                    size="sm"
                                    className="me-2"
                                    variant="outline-secondary"
                                    onClick={() => {
                                      openSubstituteForAssignment(
                                        getEntityId(item) ?? ''
                                      );
                                    }}
                                  >
                                    Substitute
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() =>
                                      deleteMutation.mutate(
                                        getEntityId(item) ?? ''
                                      )
                                    }
                                  >
                                    Delete
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
            </Accordion>
          </Card.Body>
        </Card>
      )}

      {canWrite && (
        <SubstituteAssigneeModal
          show={Boolean(substitute.assignmentId)}
          onHide={resetSubstituteModal}
          substitute={substitute}
          setSubstitute={setSubstitute}
          substituteColor={substituteColor}
          setSubstituteColor={setSubstituteColor}
          sortedAssignments={sortedAssignments}
          callOutEligible={callOutEligible}
          onSubmit={submitSubstitution}
          isSaving={substituteMutation.isLoading}
        />
      )}

      {canWrite && calendarCard}

      <PrintCalendarPdfModal
        show={printModalOpen}
        onHide={() => setPrintModalOpen(false)}
        pdfModel={pdfAllMonthsModel}
      />
    </div>
  );
};

export default CallOutCal;
